import { memo, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import { URLPaths } from 'models/enums';
import { AccessCentreRole } from 'domains/accessCentre/types';

export type RolesTableOptionsCellProps = {
    role: AccessCentreRole;
    showActions?: boolean;
};

/**
 * Memoized cell that renders the more options dropdown for a role row (View, Edit, Duplicate).
 */
const RolesTableOptionsCell = ({ role, showActions = true }: RolesTableOptionsCellProps) => {
    const history = useHistory();

    const handleView = useCallback(() => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${role.id}`);
    }, [history, role.id]);

    const handleEdit = useCallback(() => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${role.id}&mode=edit`);
    }, [history, role.id]);

    const handleDuplicate = useCallback(() => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${role.id}&isCopy=true`);
    }, [history, role.id]);

    const isSystemRoleWithoutEdit = role.system_role_type === 'admin' || role.system_role_type === 'no_access';

    const isNoAccessRole = role.system_role_type === 'no_access';

    const menuItems = useMemo(() => {
        const items: { name: string; onClick: () => void; testId: string }[] = [
            { name: 'View', onClick: handleView, testId: `access-center-role-view-${role.id}` },
        ];
        if (!isSystemRoleWithoutEdit) {
            items.push({ name: 'Edit', onClick: handleEdit, testId: `access-center-role-edit-${role.id}` });
        }
        if (!isNoAccessRole) {
            items.push({
                name: 'Duplicate',
                onClick: handleDuplicate,
                testId: `access-center-role-duplicate-${role.id}`,
            });
        }
        return items;
    }, [handleView, handleEdit, handleDuplicate, role.id, isSystemRoleWithoutEdit, isNoAccessRole]);

    if (!showActions) {
        return null;
    }

    return <MoreOptionsDropdown buttonTestId={`access-center-role-options-${role.id}`} menuItems={menuItems} />;
};

export default memo(RolesTableOptionsCell);
